import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { ApiService } from 'src/app/core/api/api.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SolicitudesPreciosAdministracionService {

  private readonly base = '/company/price-requests';
  private readonly solicitudProcesadaSubject = new ReplaySubject<any>(20);
  private readonly seguimientoSolicitudTimers = new Map<string, ReturnType<typeof setInterval>>();
  solicitudProcesada$ = this.solicitudProcesadaSubject.asObservable();

  constructor(private api: ApiService) {}

  // `token` es legacy y se ignora (el token va en el header Bearer).

  listarPendientes(_token?: any): Observable<{ rows: any[] }> {
    return this.api.post(`${this.base}/pending/list`, {});
  }

  cambiarEstado(idSolicitud: string, accion: 'aprobar' | 'rechazar', _token?: any): Observable<any> {
    return this.api.post(`${this.base}/process`, { id: idSolicitud, accion });
  }

  obtenerEstado(idSolicitud: string, userId: string, branchId: string): Observable<any> {
    return this.api.post(`${this.base}/status`, { id: idSolicitud, userId, branchId });
  }

  listarCodigosAprobadosHoy(userId: string, branchId: string, limit = 10): Observable<{ rows: any[] }> {
    return this.api.post(`${this.base}/approved/list`, { userId, branchId, limit });
  }

  conectarSocket(): Socket {
    const token = localStorage.getItem('token') || '';
    return io(`${environment.apiBaseUrl}/price-requests`, {
      auth: { token },
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });
  }

  notificarSolicitudProcesada(payload: any): void {
    this.solicitudProcesadaSubject.next(this.normalizarSolicitudProcesada(payload));
  }

  iniciarSeguimientoSolicitud(idSolicitud: string, userId: string, branchId: string): void {
    const id = String(idSolicitud || '').trim();
    const requesterUserId = String(userId || '').trim();
    const requesterBranchId = String(branchId || '').trim();
    if (!id || !requesterUserId || !requesterBranchId || this.seguimientoSolicitudTimers.has(id)) {
      return;
    }

    const consultar = () => {
      this.obtenerEstado(id, requesterUserId, requesterBranchId).subscribe({
        next: (resp: any) => {
          const data = resp?.data || resp;
          if (!data?.procesado) {
            return;
          }

          this.detenerSeguimientoSolicitud(id);
          this.notificarSolicitudProcesada(data);
        },
        error: (error: HttpErrorResponse) => {
          if ([400, 403, 404].includes(error?.status || 0)) {
            this.detenerSeguimientoSolicitud(id);
          }
          // El socket sigue siendo el canal principal. El polling es respaldo,
          // por eso no mostramos error al usuario por intentos intermedios.
        }
      });
    };

    const timer = setInterval(consultar, 2500);
    this.seguimientoSolicitudTimers.set(id, timer);
    consultar();
  }

  detenerSeguimientoSolicitud(idSolicitud: string): void {
    const id = String(idSolicitud || '').trim();
    const timer = this.seguimientoSolicitudTimers.get(id);
    if (!timer) {
      return;
    }

    clearInterval(timer);
    this.seguimientoSolicitudTimers.delete(id);
  }

  private normalizarSolicitudProcesada(payload: any): any {
    const data = payload?.data || payload || {};
    const aprobado = data.aprobado !== undefined && data.aprobado !== null
      ? Boolean(data.aprobado)
      : data.accion === 'aprobar' || data.estado === 'enviado-codigo';

    return {
      ...data,
      id: data.id ?? data.COD_SOLPREC ?? data.idSolicitud,
      aprobado,
      codigo: String(data.codigo ?? data.COD_SOLSUC ?? data.codigoConfirmacion ?? '').trim(),
      numeroSolicitud: data.numeroSolicitud ?? data.NUM_SOLPREC ?? '',
    };
  }
}
